/**
 * telemetry-privacy/D3 Extension Dimensions (spec S4): `session_id` and
 * `anonymous_id` are both random UUIDv4s from a CSPRNG. Neither is ever
 * derived from the internal user id, device id, or pseudonym.
 * `session_id` rotates after 30 minutes of idle (design.md's D3 extension
 * table); `anonymous_id` is generated once and persisted for the device's
 * lifetime.
 */

const SESSION_ID_KEY = "emmi:telemetry:session_id";
const SESSION_LAST_ACTIVITY_KEY = "emmi:telemetry:session_last_activity";
const ANONYMOUS_ID_KEY = "emmi:telemetry:anonymous_id";
const IDLE_ROTATE_MS = 30 * 60 * 1000;

export function randomUuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID (older WebViews).
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function safeGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Private-mode/blocked storage: session/anonymous ids just won't persist
    // across reloads. Never throws — telemetry must never break the app.
  }
}

export function getAnonymousId(): string {
  let id = safeGet(ANONYMOUS_ID_KEY);
  if (!id) {
    id = randomUuid();
    safeSet(ANONYMOUS_ID_KEY, id);
  }
  return id;
}

export function getSessionId(now: number = Date.now()): string {
  const lastActivity = Number(safeGet(SESSION_LAST_ACTIVITY_KEY) ?? 0);
  let id = safeGet(SESSION_ID_KEY);
  if (!id || now - lastActivity > IDLE_ROTATE_MS) {
    id = randomUuid();
    safeSet(SESSION_ID_KEY, id);
  }
  safeSet(SESSION_LAST_ACTIVITY_KEY, String(now));
  return id;
}

export function resetSession(): void {
  try {
    window.localStorage.removeItem(SESSION_ID_KEY);
    window.localStorage.removeItem(SESSION_LAST_ACTIVITY_KEY);
  } catch {
    // See safeSet.
  }
}

/**
 * usage-event-pipeline correction batch 4B item 4: `event_id` is a UUIDv7
 * (RFC 9562), not a v4 — time-ordered so BigQuery ingestion and any
 * downstream dedupe/ordering never has to fall back to `occurred_at`
 * string-sorting. Small local implementation (no added dependency): 48-bit
 * unix-ms timestamp, version nibble `7`, a 12-bit monotonic counter as
 * `rand_a` (so two ids minted in the same millisecond still sort correctly
 * relative to each other), variant bits `10`, and 62 random bits as `rand_b`.
 */
let lastEventIdTimestampMs = 0;
let lastEventIdCounter = 0;

function randomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length);
  if (typeof crypto !== "undefined" && "getRandomValues" in crypto) {
    crypto.getRandomValues(bytes);
    return bytes;
  }
  // Fallback for environments without Web Crypto's getRandomValues.
  for (let i = 0; i < length; i += 1) {
    bytes[i] = Math.floor(Math.random() * 256);
  }
  return bytes;
}

function byteAt(value: number, byteIndexFromLow: number): number {
  // `value` (a 48-bit ms timestamp here) can exceed the 32-bit range that
  // JS's `<<`/`>>>` operators support — division avoids that pitfall.
  return Math.floor(value / 2 ** (8 * byteIndexFromLow)) & 0xff;
}

function bytesToUuid(bytes: Uint8Array): string {
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function randomUuidV7(now: number = Date.now()): string {
  let ts = now;
  let counter: number;

  if (ts > lastEventIdTimestampMs) {
    counter = randomBytes(2)[0] & 0x0f; // seed low so a same-ms burst has room to increment
  } else {
    // Same millisecond as the previous id (or the clock went backwards):
    // stay on the last timestamp and bump the counter so ordering is still
    // strictly monotonic.
    ts = lastEventIdTimestampMs;
    counter = (lastEventIdCounter + 1) & 0xfff;
    if (counter === 0) {
      // Exhausted all 4096 ids for this millisecond — force time forward by
      // one so the next id still sorts after every id minted so far.
      ts += 1;
    }
  }
  lastEventIdTimestampMs = ts;
  lastEventIdCounter = counter;

  const bytes = new Uint8Array(16);
  bytes[0] = byteAt(ts, 5);
  bytes[1] = byteAt(ts, 4);
  bytes[2] = byteAt(ts, 3);
  bytes[3] = byteAt(ts, 2);
  bytes[4] = byteAt(ts, 1);
  bytes[5] = byteAt(ts, 0);
  bytes[6] = 0x70 | ((counter >> 8) & 0x0f); // version nibble (0111) + rand_a high bits
  bytes[7] = counter & 0xff; // rand_a low bits
  const tail = randomBytes(8);
  bytes[8] = 0x80 | (tail[0] & 0x3f); // variant bits (10) + rand_b high bits
  for (let i = 1; i < 8; i += 1) bytes[8 + i] = tail[i];

  return bytesToUuid(bytes);
}

export function __resetEventIdMonotonicStateForTests(): void {
  lastEventIdTimestampMs = 0;
  lastEventIdCounter = 0;
}
